import { LanguageCode } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import UButton from 'Components/unit/UButton/UButton';
import UIcon from 'Components/unit/UIcon/UIcon';
import UIllustration from 'Components/unit/UIllustration/UIllustration';
import UTextLink from 'Components/unit/UTextLink/UTextLink';
import { useActionDialogConfirmation } from 'Hooks/useActionDialogConfirmation/useActionDialogConfirmation';
import { InferPushQueueItem } from 'Libs/queue/redux/utils';
import { store, useAppDispatch } from 'Libs/redux/store';
import { redirect } from 'Libs/routing/redirect';
import ExitAIGeneration from 'Pages/Courses/Edition/components/ExitAIGeneration/ExitAIGeneration';
import MicroKnowledgeList from 'Pages/Courses/Edition/components/MicroKnowledgeList/MicroKnowledgeList';
import useAICourseView from 'Pages/Courses/Edition/hooks/useAICourseView';
import { AI_MODELS } from 'Pages/Courses/Edition/views/AIInitial/AIInitial';
import { displayAlertParams } from 'Pages/Courses/Edition/views/ViewManager/ViewManager';
import {
  actions,
  fetchDraftCourse,
  generateChapters,
  generateMicroKnowledges,
  useModuleSelector,
} from 'Pages/Courses/redux';
import { CreateUpdateCommandData } from 'Pages/Courses/utils/commands';
import {
  getHandleDeleteMk,
  getHandleInsertChapterMk,
  getHandleInsertMk,
  getHandleReorderMk,
  getHandleUpdateMk,
  type HandleInsertMkParam,
  type HandleMkDeleteParams,
  type HandleReorderMk,
  type MkQueueHandler,
} from 'Pages/Courses/utils/edition-handlers';
import { MicroKnowledgeChapter, MicroKnowledgeListType } from 'Pages/Courses/utils/mk-mapped-linked-list';
import { trackEventWithLocation } from 'Services/trackingService';
import { AI_COURSE_VIEWS } from '..';
import styles, { ILLUSTRATION_SIZE } from './AIPlanValidation.style';

import { WAIT_TIME_ON_FINISH } from '../AILoading/AILoading';

type AIPlanValidationProps = {
  push: (item: InferPushQueueItem<MkQueueHandler>) => void;
  displayAlert: (params: displayAlertParams) => void;
};

const DEFAULT_LANGUAGE = 'fr-FR';

export const AIPlanValidation: React.FC<AIPlanValidationProps> = ({ push, displayAlert }) => {
  const dispatch = useAppDispatch();

  const strings = {
    return: t('courses:ai_plan_validation.return'),
    title: t('courses:ai_plan_validation.title'),
    subtitle: t('courses:ai_plan_validation.subtitle'),
    addKnowledge: t('courses:ai_plan_validation.add_knowledge'),
    retry: t('courses:ai_plan_validation.retry'),
    validate: t('courses:ai_plan_validation.validate'),
    exit: t('courses:ai_plan_validation.exit'),
  };

  const { setViewType } = useAICourseView();
  const course = useModuleSelector((state) => state.edition.course);

  if (!course) {
    displayAlert({
      title: t('courses:errors.error'),
      text: t('courses:errors.course_initialization_error'),
      status: 'error',
      icon: 'error',
    });
    redirect('/courses');
    return null;
  }

  const mkList = useModuleSelector((state) => state.edition.microKnowledges);
  const mkListEdit = { ...mkList, items: { ...mkList.items } };

  const handleClickRestartGeneration = useCallback(async () => {
    try {
      await dispatch(
        generateChapters({
          language: (course.language as LanguageCode) || DEFAULT_LANGUAGE,
          query: course.aiPrompt || '',
          model: AI_MODELS.GPT_4,
          courseId: course.id,
        }),
      );
      setTimeout(() => {
        // This has to be delayed to be triggered after the AILoading page animations have finished
        dispatch(fetchDraftCourse(course.id));
      }, WAIT_TIME_ON_FINISH);
    } catch (error) {
      displayAlert({
        title: t('courses:errors.error'),
        text: t('courses:errors.course_generation_error'),
        status: 'error',
        icon: 'error',
      });
    }
  }, [course.aiPrompt, course.id, course.language]);

  const fetchGenerateMicroKnowledges = useCallback(async () => {
    if (!course?.aiRequestId) {
      return { headId: null, chapters: [] };
    }
    await dispatch(
      generateMicroKnowledges({
        language: (course.language as LanguageCode) || DEFAULT_LANGUAGE,
        courseId: course.id,
        model: AI_MODELS.GPT_4,
        requestId: course.aiRequestId,
      }),
    );
    setTimeout(() => {
      // This has to be delayed to be triggered after the AILoading page animations have finished
      dispatch(fetchDraftCourse(course.id));
    }, WAIT_TIME_ON_FINISH);
  }, [course?.aiRequestId, course?.id, course?.language]);

  const handleClickValidationButton = useCallback(async () => {
    try {
      await fetchGenerateMicroKnowledges();
    } catch (error) {
      displayAlert({
        title: t('courses:errors.error'),
        text: t('courses:errors.microknowledge_generation_error'),
        status: 'error',
        icon: 'error',
      });
    }
  }, [fetchGenerateMicroKnowledges]);

  const handleClickReturnToPromptButton = useCallback(() => {
    setViewType(AI_COURSE_VIEWS.INITIAL);
  }, []);

  const [baseHandleDeleteMk, baseHandleUpdateMk, baseHandleInsertMk, baseHandleInsertChapterMk, baseHandleReorderMk] =
    useMemo(
      () => [
        getHandleDeleteMk({ store, enqueue: push }),
        getHandleUpdateMk({ store, enqueue: push }),
        getHandleInsertMk({ store, enqueue: push }),
        getHandleInsertChapterMk({ store, enqueue: push }),
        getHandleReorderMk({ store, enqueue: push }),
      ],
      [push],
    );

  const handleInsertMkWithTracking = useCallback(
    (params: HandleInsertMkParam) => {
      const isCollapsedChapter =
        params.targetId &&
        mkList.items[params.targetId]?.type === 'chapter' &&
        (mkList.items[params.targetId] as MicroKnowledgeChapter)?.isCollapsed;

      isCollapsedChapter ? baseHandleInsertChapterMk(params) : baseHandleInsertMk(params);

      trackEventWithLocation('create', { context: 'ai-mk-validation' });
    },
    [baseHandleInsertMk],
  );

  const handleClickOnAddNewChapter = useCallback(() => {
    const lastItem = Object.values(mkList.items).find((item) => item.nextId === null);
    const targetId = lastItem?.id || null;
    baseHandleInsertChapterMk({
      targetId,
      title: '',
    });
    trackEventWithLocation('create-mk-chapter', { context: 'ai-plan-validation' });
  }, [mkList, baseHandleInsertMk]);

  const baseHandleUpdateMkWithTracking = useCallback(
    (params: CreateUpdateCommandData) => {
      baseHandleUpdateMk(params);
      if (mkList.items[params.id].type !== params.type) {
        trackEventWithLocation('update', {
          context: `ai-plan-validation-${params.type}`,
        });
      }
    },
    [baseHandleUpdateMk, mkList.items],
  );

  const {
    dialog: imageConversionConfirmationDialog,
    askForConfirmation: askConfirmationForImageFromChapterTypeConversion,
  } = useActionDialogConfirmation({
    title: t('courses:modal_course_image_conversion.title'),
    description: t('courses:modal_course_image_conversion.description'),
    primaryActionLabel: t('courses:modal_course_image_conversion.action'),
    primaryActionButtonType: 'destructive',
    secondaryActionLabel: t('courses:modal_course_image_conversion.secondary_action'),
  });

  const handleUpdateMk = useCallback(
    (params: CreateUpdateCommandData) => {
      if (mkList.items[params.id].type === 'chapter' && params.type === 'image') {
        askConfirmationForImageFromChapterTypeConversion(() => baseHandleUpdateMkWithTracking(params));
        return;
      }

      baseHandleUpdateMkWithTracking(params);
    },
    [askConfirmationForImageFromChapterTypeConversion, baseHandleUpdateMkWithTracking, mkList.items],
  );

  const { dialog: chapterDeleteConfirmationDialog, askForConfirmation: askConfirmationForChapterDelete } =
    useActionDialogConfirmation({
      title: t('courses:modal_course_chapter_deletion.title'),
      description: t('courses:modal_course_chapter_deletion.description'),
      primaryActionLabel: t('courses:modal_course_chapter_deletion.action'),
      primaryActionButtonType: 'destructive',
      secondaryActionLabel: t('courses:modal_course_chapter_deletion.secondary_action'),
    });

  const baseHandleDeleteMkWithTracking = useCallback(
    (params: HandleMkDeleteParams) => {
      baseHandleDeleteMk(params);
      trackEventWithLocation('delete', { context: 'ai-plan-validation' });
    },
    [baseHandleDeleteMk],
  );

  const handleDeleteMk = useCallback(
    (params: HandleMkDeleteParams) => {
      if (mkList.items[params.id].type === 'chapter') {
        askConfirmationForChapterDelete(() => baseHandleDeleteMkWithTracking(params));
        return;
      }
      baseHandleDeleteMkWithTracking(params);
    },
    [askConfirmationForChapterDelete, baseHandleDeleteMkWithTracking, mkList.items],
  );

  const handleChapterCollapse = useCallback(
    (params: { id: string }) => dispatch(actions.toggleChapterCollapse(params)),
    [dispatch],
  );

  const handleReorder = useCallback(
    (args: HandleReorderMk) => {
      const isValid = checkIsValidOrder(args);
      if (!isValid) return;

      baseHandleReorderMk(args);
      trackEventWithLocation('reorder', { context: 'ai-plan-validation' });
    },
    [baseHandleReorderMk],
  );

  const checkIsValidOrder = (itemList: HandleReorderMk) => {
    const orderedMks = [...itemList.nextOrderedMks];

    for (let i = 0; i < orderedMks.length; i++) {
      const item = orderedMks[i];
      // A mk must be in a chapter
      if (item.type !== 'chapter' && item.chapterId === null) {
        let previousChapterId = null;
        for (let j = i - 1; j >= 0; j--) {
          if (orderedMks[j].type === 'chapter') {
            previousChapterId = orderedMks[j].id;
            break;
          }
        }
        if (!previousChapterId) {
          for (let j = i + 1; j < orderedMks.length; j++) {
            if (orderedMks[j].type === 'chapter') {
              previousChapterId = orderedMks[j].id;
              break;
            }
          }
        }

        if (previousChapterId) {
          orderedMks[i] = { ...item, chapterId: previousChapterId };
        } else {
          return false;
        }
      }

      // A chapter must not be nested in another chapter
      if (item.type === 'chapter' && item.chapterId !== null) {
        orderedMks[i] = { ...item, chapterId: null };
      }
      if (item.type === 'chapter' && item.chapterId !== null) {
        return false;
      }
    }

    itemList.nextOrderedMks = orderedMks;
    return true;
  };

  const [isReturnButtonHovered, setIsReturnButtonHovered] = useState(false);
  const returnButtonCursorStyle = useMemo(
    () => (isReturnButtonHovered ? { cursor: 'pointer' } : {}),
    [isReturnButtonHovered],
  );

  const sparklesContainerRef = useRef<HTMLDivElement>(null);
  const wrapperInternalRef = useRef<HTMLDivElement>(null);

  const resizeHandler = () => {
    if (!sparklesContainerRef.current || !wrapperInternalRef.current) return;
    const sparklesContainer = sparklesContainerRef.current;
    const wrapperInternal = wrapperInternalRef.current;

    const wrapperInternalRect = wrapperInternal.getBoundingClientRect();
    const sparklesContainerRect = sparklesContainer.getBoundingClientRect();

    const sparklesTopOffset = wrapperInternalRect.top + 18;
    const sparklesLeftOffset = wrapperInternalRect.left + wrapperInternalRect.width - sparklesContainerRect.width;
    sparklesContainer.style.top = `${sparklesTopOffset}px`;
    sparklesContainer.style.left = `${sparklesLeftOffset}px`;
  };

  useEffect(() => {
    Object.values(mkList.items).forEach((item) => {
      if (item.type === 'chapter' && item.isCollapsed) {
        dispatch(actions.toggleChapterCollapse({ id: item.id }));
      }
    });
  }, []);

  useLayoutEffect(() => {
    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  return (
    <div style={styles.wrapperExternal}>
      <div ref={wrapperInternalRef} style={styles.wrapperInternal}>
        <div ref={sparklesContainerRef} id="illustrationContainer" style={styles.illustrationContainer}>
          <UIllustration illustration="sparkles" height={ILLUSTRATION_SIZE.height} width={ILLUSTRATION_SIZE.width} />
        </div>
        <div id="header" style={styles.headerContainer}>
          <div
            id="return-button"
            style={{ ...styles.returnButton, ...returnButtonCursorStyle }}
            onMouseEnter={() => setIsReturnButtonHovered(true)}
            onMouseLeave={() => setIsReturnButtonHovered(false)}
            onClick={handleClickReturnToPromptButton}
          >
            <UIcon name="keyboard-return" />
            <div>{strings.return}</div>
          </div>
          <div style={styles.title}>{strings.title}</div>
          <div style={styles.subtitle}>{strings.subtitle}</div>
        </div>
        <div id="body" style={{ ...styles.bodyContainer, zIndex: 1 }}>
          <div id="body-top"></div>
          <div id="body-content">
            <MicroKnowledgeList
              microKnowledgeMap={mkListEdit as unknown as MicroKnowledgeListType}
              onDelete={handleDeleteMk}
              onInsert={handleInsertMkWithTracking}
              onToggleCollapse={handleChapterCollapse}
              onChange={handleUpdateMk}
              onDuplicate={() => {}}
              onReorder={handleReorder}
              hideContent={true}
              isGeneratedContent={true}
            />
          </div>
          <div id="body-bottom" style={styles.bodyBottomContainer}>
            <UTextLink
              leftIcon="plus-light"
              text={strings.addKnowledge}
              style={styles.AddMkButton}
              onClick={handleClickOnAddNewChapter}
            />
          </div>
        </div>
        <div id="footer" style={styles.footerContainer}>
          <UButton
            type="ai"
            ghost={true}
            leftIcon="refreshWithGradient"
            onClick={handleClickRestartGeneration}
            text={strings.retry}
            typography={'BODY3_SEMIBOLD'}
          />
          <UButton
            type="ai"
            ghost={false}
            leftIcon="sparkles"
            onClick={handleClickValidationButton}
            text={strings.validate}
            typography={'BODY3_SEMIBOLD'}
          />
        </div>
      </div>
      <div style={styles.exitButtonContainer}>
        <ExitAIGeneration />
      </div>
      {chapterDeleteConfirmationDialog}
      {imageConversionConfirmationDialog}
    </div>
  );
};

export default AIPlanValidation;
